import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AddStoreComponent } from '../../core/add-store/add-store.component';
import { ServiceService } from '../../../services/service.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-company-admin-page',
  templateUrl: './company-admin-page.component.html',
  styleUrls: ['./company-admin-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatCardModule,
    MatTabsModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    AddStoreComponent
  ]
})
export class CompanyAdminPageComponent implements OnInit {
  companyName?: string;
  companyId?: string;
  stores: any[] = [];
  displayedColumns: string[] = ['name', 'email', 'phone', 'location', 'siteLocation', 'actions'];
  dataSource = new MatTableDataSource<any>();
  
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  
  selectedStore?: any;
  isLoading = false;
  showAddStoreForm = false;
  showEditModal = false;
  showEditCompanyModal = false;
  editingStore: any = {};
  editingCompany: any = {};
  company: any = null;
  selectedTabIndex = 1; // Default to "All Stores" tab
  siteLocationOptions: { value: string; label: string }[] = [];
  
  // Signal for recent stores (last 4)
  recentStores() {
    return this.stores.slice(0, 4);
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private service: ServiceService,
    private snackBar: MatSnackBar,
    private authService: AuthService
  ) { }

  ngOnInit() {
    this.loadSiteLocationOptions();
    // Check if companyId is provided in query params (for admin navigation)
    const companyId = this.route.snapshot.queryParams['companyId'];
    const hasCompanyIdParam = !!companyId;
    
    // Check which tab to show based on route data
    const tab = this.route.snapshot.data['tab'];
    
    // If companyId is in query params, always show Dashboard tab
    // Otherwise, show based on route data
    if (hasCompanyIdParam) {
      this.selectedTabIndex = 0; // Dashboard tab (to show company info)
    } else if (tab === 'stores') {
      this.selectedTabIndex = 1; // All Stores tab
    } else {
      this.selectedTabIndex = 0; // Dashboard tab (default)
    }
    
    if (companyId) {
      this.loadCompanyByAdmin(companyId);
    } else {
      this.loadCompanyData();
    }
  }

  loadSiteLocationOptions() {
    this.service.getSiteConfigurations().subscribe({
      next: (configs) => {
        const locations = [...new Set(configs.map((c: any) => c.location).filter(Boolean))].sort();
        this.siteLocationOptions = locations.length > 0
          ? locations.map((loc: string) => ({ value: loc, label: loc.charAt(0).toUpperCase() + loc.slice(1) }))
          : [{ value: 'default', label: 'Default' }];
      },
      error: () => {
        this.siteLocationOptions = [{ value: 'default', label: 'Default' }];
      }
    });
  }

  ngAfterViewInit() {
    if (this.dataSource) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
  }

  loadCompanyByAdmin(companyId: string) {
    console.log('loadCompanyByAdmin called with companyId:', companyId);
    this.isLoading = true;
    this.companyId = companyId;
    
    this.service.getCompanyById(companyId).subscribe({
      next: (company) => {
        console.log('Company loaded in loadCompanyByAdmin:', company);
        if (company) {
          this.company = company;
          this.companyName = company.name;
          console.log('Company set on component:', this.company);
        } else {
          console.warn('Company is null or undefined');
        }
        // Load stores after company loads
        this.service.getStoresByCompanyId(companyId).subscribe({
          next: (stores) => {
            this.stores = stores;
            this.dataSource.data = stores;
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Error loading stores:', error);
            this.showMessage('Error loading stores', 'error');
            this.isLoading = false;
          }
        });
      },
      error: (err) => {
        console.error('Error loading company in loadCompanyByAdmin:', err);
        console.error('Error details:', JSON.stringify(err, null, 2));
        this.showMessage('Error loading company information', 'error');
        this.isLoading = false;
      }
    });
  }

  async loadCompanyData() {
    try {
      this.isLoading = true;
      const user = await this.authService.getCurrentUser();
      
      // Get user's associated company
      this.service.getUsers().subscribe({
        next: async (users) => {
          const currentUser = users.find(u => u.email === user.username);
          
          if (currentUser && currentUser.companyID) {
            this.companyId = currentUser.companyID;
            
            // Load company details
            this.service.getCompanyById(currentUser.companyID).subscribe({
              next: (company) => {
                console.log('Company loaded in loadCompanyData:', company);
                if (company) {
                  this.company = company;
                  this.companyName = company.name;
                  console.log('Company set on component:', this.company);
                } else {
                  console.warn('Company is null or undefined');
                }
                // Load stores for this company
                this.service.getStoresByCompanyId(currentUser.companyID).subscribe({
                  next: (stores) => {
                    this.stores = stores;
                    this.dataSource.data = stores;
                    this.isLoading = false;
                  },
                  error: (error) => {
                    console.error('Error loading stores:', error);
                    this.showMessage('Error loading stores', 'error');
                    this.isLoading = false;
                  }
                });
              },
              error: (err) => {
                console.error('Error loading company:', err);
                this.showMessage('Error loading company information', 'error');
                this.isLoading = false;
              }
            });
          } else {
            this.showMessage('Could not find your company information', 'error');
            this.isLoading = false;
          }
        },
        error: (err) => {
          console.error('Error loading users:', err);
          this.showMessage('Error loading user data', 'error');
          this.isLoading = false;
        }
      });
    } catch (error) {
      console.error('Error loading company data:', error);
      this.showMessage('Error loading company data', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  loadStores() {
    console.log('loadStores called with companyId:', this.companyId);
    if (!this.companyId) {
      console.warn('No companyId available');
      this.isLoading = false;
      return;
    }
    
    this.service.getStoresByCompanyId(this.companyId).subscribe({
      next: (stores) => {
        console.log('Stores loaded in component:', stores);
        this.stores = stores;
        this.dataSource.data = stores;
        console.log('DataSource updated with stores:', this.dataSource.data);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading stores:', error);
        this.showMessage('Error loading stores', 'error');
        this.isLoading = false;
      }
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onStoreAdded() {
    // Refresh the stores list after a new store is added
    if (this.companyId) {
      this.loadStores();
    }
    // Note: showAddStoreForm is handled by the add-store component
    this.showMessage('Store added successfully', 'success');
  }

  openEditStoreModal(store: any) {
    this.editingStore = { ...store };
    if (!this.editingStore.siteLocation && this.siteLocationOptions.length > 0) {
      this.editingStore.siteLocation = this.siteLocationOptions[0].value;
    } else if (!this.editingStore.siteLocation) {
      this.editingStore.siteLocation = 'default';
    }
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editingStore = {};
  }

  saveEditedStore() {
    if (!this.editingStore.id) return;

    this.service.updateStore(this.editingStore.id, {
      name: this.editingStore.name,
      location: this.editingStore.location,
      siteLocation: this.editingStore.siteLocation,
      email: this.editingStore.email,
      phone: this.editingStore.phone,
      description: this.editingStore.description
    }).subscribe({
      next: () => {
        this.showMessage('Store updated successfully', 'success');
        this.loadStores();
        this.closeEditModal();
      },
      error: (error) => {
        console.error('Error updating store:', error);
        this.showMessage('Error updating store', 'error');
      }
    });
  }

  editStore(store: any) {
    this.openEditStoreModal(store);
  }

  viewStoreDetails(store: any) {
    // Navigate to store page
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/store/' + store.id])
    );
    window.open(url, '_blank');
  }

  deleteStore(store: any) {
    if (!confirm(`Are you sure you want to delete "${store.name}"? This action cannot be undone.`)) {
      return;
    }

    this.service.deleteStore(store.id).subscribe({
      next: () => {
        this.showMessage('Store deleted successfully', 'success');
        this.loadStores();
      },
      error: (error) => {
        console.error('Error deleting store:', error);
        this.showMessage('Error deleting store', 'error');
      }
    });
  }

  openEditCompanyModal() {
    console.log('Opening edit company modal, company:', this.company);
    if (this.company) {
      this.editingCompany = { ...this.company };
      this.showEditCompanyModal = true;
      console.log('Modal should now be visible, showEditCompanyModal:', this.showEditCompanyModal);
    } else {
      console.warn('No company data available');
      this.showMessage('Company data not loaded yet', 'info');
    }
  }

  closeEditCompanyModal() {
    this.showEditCompanyModal = false;
    this.editingCompany = {};
  }

  saveEditedCompany() {
    if (!this.company?.id) return;

    this.service.updateCompany(this.company.id, {
      name: this.editingCompany.name,
      contactName: this.editingCompany.contactName,
      contactEmail: this.editingCompany.contactEmail,
      contactPhone: this.editingCompany.contactPhone,
      website: this.editingCompany.website,
      description: this.editingCompany.description
    }).subscribe({
      next: (updatedCompany) => {
        this.company = updatedCompany;
        this.companyName = updatedCompany.name;
        this.showMessage('Company information updated successfully', 'success');
        this.closeEditCompanyModal();
      },
      error: (error) => {
        console.error('Error updating company:', error);
        this.showMessage('Error updating company information', 'error');
      }
    });
  }

  showMessage(message: string, type: 'success' | 'error' | 'info' = 'info') {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: [`${type}-snackbar`]
    });
  }
}
